import React, { useEffect } from 'react';
// internal imports
import { useSetState } from '~/app/Utility/customHooks';
// external imports
import {
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Input,
  Divider,
  Button,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Grid,
} from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
// redux imports
import { useSelector, useDispatch } from 'react-redux';
import { actions as setupPageActions } from '~/app/redux/setupPages/duck';
import { formHasErrors } from '../databaseSetupHelper';

const UpsertEduInst = props => {
  const dispatch = useDispatch();

  const { isEditingRecord, currentEditRecord } = useSelector(state => ({
    isEditingRecord: state.setupPageState.isEditingRecord,
    currentEditRecord: state.setupPageState.currentEditRecord,
  }));

  const [state, setState] = useSetState({
    expandedUSAddress: isEditingRecord,
    expandedForeignAddress: isEditingRecord,
    foreignAddress: currentEditRecord.hidden_domestic_ind,
    inputErrors: {},
    isSubmitDisabled: false,
  });

  useEffect(() => {
    setState({
      isSubmitDisabled: formHasErrors(currentEditRecord, handleSetErrors),
    });
  }, [currentEditRecord]);

  const handleSetErrors = errors => {
    setState({ inputErrors: errors }); // set any errors to the local state
  };

  useEffect(() => {
    if (
      currentEditRecord.hidden_domestic_ind === undefined &&
      currentEditRecord.hidden_foreign_ind === undefined
    ) {
      dispatch(setupPageActions.setHiddenForeignInd());
    }
  }, []);

  const handleInputChange = e => {
    const name = e.target.name;
    e.persist();
    dispatch(setupPageActions.updateCurrentEditRecord(e));
    // Clear out inputErrors if user is modifying field value
    const tempErrors = state.inputErrors;
    if (tempErrors[name]) {
      delete tempErrors[name];
      setState({
        inputErrors: tempErrors,
      });
    }
  };

  const handleUpsert = () => {
    props.handleUserDBUpsert(currentEditRecord);
  };

  const handleDomesticCheckbox = e => {
    setState({ foreignAddress: e.target.checked });
    if (e.target.checked) {
      dispatch(setupPageActions.clearAddressData(false, true));
    } else {
      dispatch(setupPageActions.clearAddressData(true, false));
    }
  };

  const validateFields = () => {
    if (
      currentEditRecord.address1 == null ||
      currentEditRecord.address1 === '' ||
      currentEditRecord.postal_code == null ||
      currentEditRecord.postal_code === '' ||
      currentEditRecord.institution_ein == null ||
      currentEditRecord.institution_ein === '' ||
      currentEditRecord.institution_name == null ||
      currentEditRecord.institution_name === ''
    ) {
      return false;
    }

    // Province may be undef since its not loaded initially
    if (
      (state.foreignAddress &&
        (typeof currentEditRecord.province === 'undefined' || currentEditRecord.province === '')) ||
      (!state.foreignAddress && currentEditRecord.state_code === '')
    ) {
      return false;
    }

    return true;
  };

  return (
    <div>
      <AppBar position="static">
        <Toolbar style={styles.toolbar}>
          {isEditingRecord ? 'Edit ' : 'Add New '}
          Educational Institution
        </Toolbar>
      </AppBar>
      <form styles={styles.form}>
        <Grid container>
          <Grid container spacing={8} style={{ marginBottom: '2em', textAlign: 'left' }}>
            <Grid item xs={8} style={styles.sectionHeading}>
              EIN Information
            </Grid>
            <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
              <FormControl>
                <InputLabel style={styles.labelStyles} required={false} shrink>
                  Federal EIN
                </InputLabel>
                <Input
                  id="txtEINIDUpsertEduInstEIN"
                  name="institution_ein"
                  value={currentEditRecord.institution_ein}
                  onChange={handleInputChange}
                  style={styles.inputStyle}
                  error={
                    !currentEditRecord.institution_ein ||
                    currentEditRecord.institution_ein === '' ||
                    state.inputErrors?.institution_ein !== undefined
                  }
                  inputProps={{
                    maxLength: '9',
                  }}
                  disableUnderline
                />
                {state.inputErrors?.institution_ein && (
                  <FormHelperText>{state.inputErrors.institution_ein}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item style={{ float: 'right' }}>
              <FormControl>
                <InputLabel style={styles.labelStyles} required={false} shrink>
                  Institution Name
                </InputLabel>
                <Input
                  id="txtNameControlUpsertEduInstName"
                  name="institution_name"
                  value={currentEditRecord.institution_name}
                  onChange={handleInputChange}
                  style={styles.inputStyle}
                  error={
                    !currentEditRecord.institution_name ||
                    currentEditRecord.institution_name === '' ||
                    state.inputErrors?.institution_name !== undefined
                  }
                  inputProps={{
                    maxLength: '75',
                  }}
                  disableUnderline
                />
                {state.inputErrors?.institution_name && (
                  <FormHelperText>{state.inputErrors.institution_name}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Divider style={{ marginTop: '1em' }} />
        <Grid container spacing={8} style={{ textAlign: 'left', flexWrap: 'nowrap' }}>
          <Grid item xs={8} style={styles.sectionHeading}>
            {state.foreignAddress ? 'Foreign Address' : 'Domestic Address'}
          </Grid>
          {!isEditingRecord && (
            <Grid item xs={4} style={{ paddingTop: '1.5em' }}>
              <FormControlLabel
                label="Use Foreign Address"
                control={
                  <Checkbox
                    style={{ width: 17, height: 16, textAlign: 'right' }}
                    icon={<CheckBoxOutlineBlankIcon style={{ fontSize: 24 }} color="secondary" />}
                    checkedIcon={<CheckBoxIcon style={{ fontSize: 24 }} color="primary" />}
                    id="chkDomesticAddrEduInst"
                    name="domesticCheckboxEduInst"
                    checked={state.foreignAddress}
                    onChange={handleDomesticCheckbox}
                    inputProps={{ height: '20px', width: '20px' }}
                  />
                }
              />
            </Grid>
          )}
        </Grid>

        {(currentEditRecord.hidden_foreign_ind === true || !isEditingRecord) && (
          <Grid container>
            {!state.foreignAddress && (
              <Grid container spacing={8} style={{ textAlign: 'left' }}>
                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Care of Address
                    </InputLabel>
                    <Input
                      id="txtEINIDUpsertEduInstCOAddress"
                      name="address2"
                      value={currentEditRecord.address2}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      inputProps={{
                        maxLength: '75',
                      }}
                      error={state.inputErrors?.address2 !== undefined}
                      disableUnderline
                    />
                    {state.inputErrors?.address2 && (
                      <FormHelperText>{state.inputErrors.address2}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'right' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Institution Address
                    </InputLabel>
                    <Input
                      id="txtEINIDUpsertEduInstFirmAddress"
                      name="address1"
                      value={currentEditRecord.address1}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.address1 ||
                        currentEditRecord.address1 === '' ||
                        state.inputErrors?.address1 !== undefined
                      }
                      inputProps={{
                        maxLength: '75',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.address1 && (
                      <FormHelperText>{state.inputErrors.address1}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      City
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEduInstCity"
                      name="city"
                      value={currentEditRecord.city}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.city ||
                        currentEditRecord.city === '' ||
                        state.inputErrors?.city !== undefined
                      }
                      inputProps={{
                        maxLength: '75',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.city && (
                      <FormHelperText>{state.inputErrors.city}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'right' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} shrink>
                      State
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEduInstState"
                      name="state_code"
                      value={currentEditRecord.state_code || ''}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      inputProps={{
                        maxLength: '2',
                      }}
                      error={
                        !currentEditRecord.state_code || state.inputErrors?.state_code !== undefined
                      }
                      disableUnderline
                    />
                    <FormHelperText>{state.inputErrors?.state_code}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={6} style={{ marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Zip Code
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEduInstZipCode"
                      name="postal_code"
                      value={currentEditRecord.postal_code}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.postal_code ||
                        currentEditRecord.postal_code === '' ||
                        state.inputErrors?.postal_code !== undefined
                      }
                      inputProps={{
                        maxLength: '12',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.postal_code && (
                      <FormHelperText>{state.inputErrors.postal_code}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}

        {(currentEditRecord.hidden_domestic_ind === true || !isEditingRecord) && (
          <Grid container>
            {state.foreignAddress && (
              <Grid container spacing={8} style={{ textAlign: 'left' }}>
                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Address 1
                    </InputLabel>
                    <Input
                      id="txtEINIDUpsertEduInstAddress1"
                      name="address1"
                      value={currentEditRecord.address1}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.address1 ||
                        currentEditRecord.address1 === '' ||
                        state.inputErrors?.address1 !== undefined
                      }
                      inputProps={{
                        maxLength: '75',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.address1 && (
                      <FormHelperText>{state.inputErrors.address1}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'right' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Address 2
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEduInstAddress2"
                      name="address2"
                      value={currentEditRecord.address2}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      inputProps={{
                        maxLength: '75',
                      }}
                      error={state.inputErrors?.address2 !== undefined}
                      disableUnderline
                    />
                    {state.inputErrors?.address2 && (
                      <FormHelperText>{state.inputErrors.address2}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      City
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEduInstCity"
                      name="city"
                      value={currentEditRecord.city}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.city ||
                        currentEditRecord.city === '' ||
                        state.inputErrors?.city !== undefined
                      }
                      inputProps={{
                        maxLength: '75',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.city && (
                      <FormHelperText>{state.inputErrors.city}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'right' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Province
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEduInstProvince"
                      name="province"
                      value={currentEditRecord.province}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={!currentEditRecord.province || currentEditRecord.province === ''}
                      inputProps={{
                        maxLength: '75',
                      }}
                      disableUnderline
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={6} style={{ float: 'left', marginLeft: '1em' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Postal Code
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEduInstPostalCode"
                      name="postal_code"
                      value={currentEditRecord.postal_code}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      error={
                        !currentEditRecord.postal_code ||
                        currentEditRecord.postal_code === '' ||
                        state.inputErrors?.postal_code !== undefined
                      }
                      inputProps={{
                        maxLength: '50',
                      }}
                      disableUnderline
                    />
                    {state.inputErrors?.postal_code && (
                      <FormHelperText>{state.inputErrors.postal_code}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item style={{ float: 'right' }}>
                  <FormControl>
                    <InputLabel style={styles.labelStyles} required={false} shrink>
                      Country Code
                    </InputLabel>
                    <Input
                      id="txtNameControlUpsertEduInstCountryCode"
                      name="country_code"
                      value={currentEditRecord.country_code}
                      onChange={handleInputChange}
                      style={styles.inputStyle}
                      inputProps={{
                        maxLength: '2',
                      }}
                      disableUnderline
                    />
                  </FormControl>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
        <Grid container spacing={8} style={styles.bottomButtons}>
          <Grid item xs={9}>
            <Button
              id="btnCancelAddUUpsertEduInst"
              color="primary"
              onClick={props.handleCloseUpsertModal}
              style={styles.button}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              id="btnUpsertEduInst"
              color="primary"
              style={styles.button}
              onClick={handleUpsert}
              disabled={!validateFields() || state.isSubmitDisabled}
            >
              {isEditingRecord ? 'Save' : 'Add'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </div>
  );
};

const styles = {
  toolbar: {
    backgroundColor: '#444A59',
  },
  sectionHeading: {
    color: '#1A173B',
    fontFamily: 'Roboto',
    fontSize: '21px',
    paddingTop: '1em',
    marginLeft: '1em',
    marginBottom: '1em',
  },
  form: {
    margin: '2em',
  },
  button: {
    width: '9em',
    fontSize: '14px',
    borderRadius: '4px',
    marginLeft: '2em',
  },
  labelStyles: {
    color: '#354052',
    textAlign: 'left',
    fontFamily: 'Roboto',
  },
  inputStyle: {
    width: '20vw',
  },
  bottomButtons: {
    textAlign: 'right',
    padding: '0',
    marginRight: '2em',
    marginBottom: '2em',
    marginTop: '2em',
    flexWrap: 'nowrap',
  },
};

export default UpsertEduInst;
